import { Col, Form, Input, Modal, Row, Select, Switch, TimePicker, Upload, UploadFile } from 'antd'
import { useFormik } from 'formik'
import { NumericFormat } from 'react-number-format'
import dayjs from 'dayjs'
import {
  AdminsEcommerceInventoryStockLocationCreateRequest,
  AdminsFinanceGetVendorListResponseItem,
  Configuration,
  MediaApi,
  ProvinceResponseItem,
  StockLocationStatusEnum,
} from 'generated/api'
import Button from 'components/button/Button'
import { IconCancel } from 'components/icons'
import Card from 'components/card/Card'
import ErrorHintForm from 'components/errors/ErrorHintForm'
import RequiredMark from 'components/form/RequiredMark'
import { validationPhoneNumber, validationSchemaFormStockLocation } from 'helpers/validation'
import { StateFormEnum } from '../../hooks/useStockLocation'
import React, { useEffect, useState } from 'react'
import { DocumentDownload, Image } from 'iconsax-react'
import type { RcFile, UploadProps } from 'antd/lib/upload'
import axios from 'axios'
import { axiosDefaultConfig } from 'constants/axios'
import { isImage, isSizeMaxAllowed, isSizeMinAllowed } from 'helpers/imageUpload'
import { classNameInput } from 'helpers/formik/classNameForm'

interface IProps {
  isOpen: boolean
  isLoading: boolean
  isLoadingVendors: boolean
  stateForm?: StateFormEnum
  initialValues: AdminsEcommerceInventoryStockLocationCreateRequest
  vendorInitialName: string
  provinces: ProvinceResponseItem[]
  vendors: AdminsFinanceGetVendorListResponseItem[]
  onChangeVendorNameDropdown: (value: string) => void
  onCancel: (value: boolean) => void
  onSubmit: (
    values: AdminsEcommerceInventoryStockLocationCreateRequest,
    stateForm: StateFormEnum,
  ) => void
  onPopUpScrollVendors: (event: React.UIEvent<HTMLDivElement>) => void
}

const axiosConfig = new Configuration(axiosDefaultConfig)
const mediaApi = new MediaApi(axiosConfig)

function ModalFormStockLocation(props: IProps) {
  const {
    isOpen,
    isLoading,
    isLoadingVendors,
    stateForm = StateFormEnum.CREATE,
    initialValues,
    vendorInitialName,
    provinces,
    vendors,
    onChangeVendorNameDropdown,
    onCancel,
    onSubmit,
    onPopUpScrollVendors,
  } = props

  const [fileList, setFileList] = useState<UploadFile[]>([])

  const title =
    stateForm === StateFormEnum.CREATE ? 'Tambah Lokasi Pengambilan' : 'Ubah Lokasi Pengambilan'

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchemaFormStockLocation,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: values => {
      onSubmit(values, stateForm)
    },
  })

  useEffect(() => {
    if (formik.values.imageUrlItems?.length) {
      const updatedFileList = formik.values.imageUrlItems.map((item, index) => {
        return {
          uid: index,
          name: item,
          status: 'done',
          url: item,
        }
      })
      setFileList(updatedFileList as unknown as UploadFile[])
    }
  }, [formik.values.imageUrlItems])

  const imageProps: UploadProps = {
    multiple: false,
    fileList: fileList,
    listType: 'picture',
    iconRender: () => {
      return <Image size={24} />
    },
    onRemove: file => {
      const updatedFiles = fileList.filter(item => item.uid !== file.uid)
      setFileList(updatedFiles as UploadFile[])
      formik.setFieldValue(
        'imageUrlItems',
        updatedFiles.map(item => item.url),
      )
    },
    beforeUpload: async (file: RcFile) => {
      const isImageFile = isImage(file)
      const isSizeMinAllowedFile = isSizeMinAllowed(file)
      const isSizeMaxAllowedFile = isSizeMaxAllowed(file)

      if (!isImageFile) {
        await formik.setFieldTouched('imageUrlItems', true)
        formik.setFieldError(
          'imageUrlItems',
          `${file.name} tidak valid. Hanya menerima file dengan format PNG, JPG, JPEG`,
        )
        return false
      }

      if (!isSizeMinAllowedFile) {
        await formik.setFieldTouched('imageUrlItems', true)
        formik.setFieldError('imageUrlItems', `${file.name} minimal ukuran lebih dari 1KB`)
        return false
      }

      if (!isSizeMaxAllowedFile) {
        await formik.setFieldTouched('imageUrlItems', true)
        formik.setFieldError('imageUrlItems', `${file.name} melebihi ukuran maksimal 2MB`)
        return false
      }

      if (fileList.length >= 2) {
        await formik.setFieldTouched('imageUrlItems', true)
        formik.setFieldError(
          'imageUrlItems',
          'Melebihi 2 files yang diupload. Hapus salah satu file',
        )
        return false
      }

      return (isImageFile && isSizeMaxAllowedFile && isSizeMinAllowedFile) || Upload.LIST_IGNORE
    },
    customRequest: async ({ file }) => {
      const uploadFile = file as RcFile
      const updatedFileList = [
        ...fileList,
        { uid: uploadFile.uid, name: uploadFile.name, status: 'uploading', percent: 0 },
      ]
      try {
        setFileList(updatedFileList as UploadFile[])
        const date = dayjs().toDate().valueOf()
        const responsePresign = await mediaApi.mediaPresignedUrl({
          operationType: 'Ecommerce',
          mimeType: uploadFile.type,
          originalFileName: `stores/${date}`,
          fileSize: uploadFile.size,
        })
        await axios.put(responsePresign.data.presignedUrl, file, {
          headers: {
            'Content-Type': uploadFile.type,
          },
          onUploadProgress: progressEvent => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              )
              const updatedFileListProgress = updatedFileList.map(item => {
                if (item.uid === uploadFile.uid) {
                  return { ...item, percent: percentCompleted }
                }
                return item
              })
              setFileList(updatedFileListProgress as UploadFile[])
            }
          },
        })

        const updatedFileListDone = updatedFileList.map(item => {
          if (item.uid === uploadFile.uid) {
            return {
              uid: uploadFile.uid,
              name: uploadFile.name,
              status: 'done',
              url: responsePresign.data.publicUrl,
            }
          }
          return item
        })
        const updatedImageUrlItems = formik.values.imageUrlItems?.concat(
          responsePresign.data.publicUrl,
        )
        setFileList(updatedFileListDone as UploadFile[])
        await formik.setFieldValue('imageUrlItems', updatedImageUrlItems)
      } catch (err) {
        const updatedFileListError = updatedFileList.map(item => {
          if (item.uid === uploadFile.uid) {
            return { ...item, status: 'error' }
          }
          return item
        })
        setFileList(updatedFileListError as UploadFile[])
      }
    },
  }

  return (
    <Modal
      data-testid="modal_form_stock_location"
      className="modal-admin pt-5"
      closable={!isLoading}
      maskClosable={!isLoading}
      onCancel={() => onCancel(false)}
      centered
      title={title}
      open={isOpen}
      footer={
        <div className="flex justify-end">
          <Button
            type="submit"
            loading={isLoading}
            block={true}
            onClick={formik.handleSubmit}
            data-testid="btn_accept"
            className="pt-3 rounded-xl pb-3 font-bold text-sm w-auto"
            iconPosition="right"
            label="SIMPAN"
            variant="solid-primary"
          />
        </div>
      }
      width={1000}
      closeIcon={<IconCancel strokeWidth="2" color="black" />}
    >
      <Card
        loading={isLoading}
        dataTestId="card_stock_location_form"
        spinnerTestId="loader_stock_location_form"
        className="rounded-xl p-0"
      >
        <div className="flex flex-col">
          <Form className="form" layout="vertical" preserve={false} requiredMark={RequiredMark}>
            <div className="xl:flex xl:gap-4 block">
              <div className="xl:w-1/2">
                <div>
                  <Form.Item
                    required
                    name="name"
                    label="Nama Lokasi / Toko"
                    className="font-bold mb-1"
                    valuePropName="name"
                  >
                    <Input
                      data-testid="input_name"
                      placeholder="Contoh: PMKS Buatan Satu"
                      className={classNameInput('name', formik)}
                      status={formik.errors.name && formik.touched.name ? 'error' : ''}
                      onChange={e => formik.setFieldValue('name', e.target.value)}
                      value={formik.values.name}
                    />
                  </Form.Item>
                  {formik.errors.name && formik.touched.name ? (
                    <ErrorHintForm
                      errorMessage={formik.errors.name}
                      dataTestId="error_message_input_name"
                    />
                  ) : null}
                </div>
                <Form.Item
                  name="referenceLocation"
                  label="Nama Alias / Patokan"
                  className="font-bold mb-1"
                  valuePropName="referencelocation"
                >
                  <Input
                    data-testid="input_referenceLocation"
                    placeholder="Contoh: Sebrang Sekolah"
                    className={classNameInput('referenceLocation', formik)}
                    onChange={e => formik.setFieldValue('referenceLocation', e.target.value)}
                    value={formik.values.referenceLocation}
                  />
                </Form.Item>

                <div>
                  <Form.Item
                    name="vendorId"
                    label="Pilih Vendor"
                    className="font-bold mb-1"
                    valuePropName="vendorid"
                  >
                    <Select
                      id="vendorid"
                      showSearch
                      data-testid="select_vendorId"
                      className="selector h-full"
                      placeholder="Pilih Vendor"
                      virtual={false}
                      filterOption={false}
                      loading={isLoadingVendors}
                      onSearch={value => onChangeVendorNameDropdown(value)}
                      onPopupScroll={onPopUpScrollVendors}
                      onChange={value => formik.setFieldValue('vendorId', value)}
                      value={
                        vendors.find(vendor => vendor.id === formik.values.vendorId)
                          ? formik.values.vendorId
                          : vendorInitialName || null
                      }
                    >
                      {vendors.map(vendor => {
                        return (
                          <Select.Option
                            data-testid={`select_vendor_id_${vendor.id}`}
                            key={vendor.id}
                            value={vendor.id}
                          >
                            {vendor.name}
                          </Select.Option>
                        )
                      })}
                    </Select>
                  </Form.Item>
                </div>
                <div>
                  <Form.Item
                    required
                    name="ownerName"
                    label="Nama Pemilik"
                    className="font-bold mb-1"
                    valuePropName="ownername"
                  >
                    <Input
                      data-testid="input_ownerName"
                      placeholder="Contoh: Pabrik Buatan Satu"
                      className={classNameInput('ownerName', formik)}
                      status={formik.errors.ownerName && formik.touched.ownerName ? 'error' : ''}
                      onChange={e => formik.setFieldValue('ownerName', e.target.value)}
                      value={formik.values.ownerName}
                    />
                  </Form.Item>
                  {formik.errors.ownerName && formik.touched.ownerName ? (
                    <ErrorHintForm
                      errorMessage={formik.errors.ownerName}
                      dataTestId="error_message_input_ownerName"
                    />
                  ) : null}
                </div>
                <div>
                  <Form.Item
                    required
                    name="ownerPhoneNo"
                    label="Nomor Telepon Pemilik"
                    className="font-bold mb-1"
                    valuePropName="ownerphoneno"
                  >
                    <Input
                      data-testid="input_ownerPhoneNo"
                      placeholder="Contoh: 081213454345"
                      className={classNameInput('ownerPhoneNo', formik)}
                      status={
                        formik.errors.ownerPhoneNo && formik.touched.ownerPhoneNo ? 'error' : ''
                      }
                      onChange={e => {
                        const isPhoneNumber = validationPhoneNumber(e)
                        if (isPhoneNumber) formik.setFieldValue('ownerPhoneNo', e.target.value)
                      }}
                      value={formik.values.ownerPhoneNo}
                    />
                  </Form.Item>
                  {formik.errors.ownerPhoneNo && formik.touched.ownerPhoneNo ? (
                    <ErrorHintForm
                      errorMessage={formik.errors.ownerPhoneNo}
                      dataTestId="error_message_input_ownerPhoneNo"
                    />
                  ) : null}
                </div>
                <div>
                  <Form.Item
                    required
                    name="address"
                    label="Alamat Lokasi"
                    className="font-bold mb-1"
                    valuePropName="address"
                  >
                    <Input
                      data-testid="input_address"
                      placeholder="Contoh: Jl. Sudirman No 08 KM 78"
                      className={classNameInput('address', formik)}
                      status={formik.errors.address && formik.touched.address ? 'error' : ''}
                      onChange={e => formik.setFieldValue('address', e.target.value)}
                      value={formik.values.address}
                    />
                  </Form.Item>
                  {formik.errors.address && formik.touched.address ? (
                    <ErrorHintForm
                      errorMessage={formik.errors.address}
                      dataTestId="error_message_input_address"
                    />
                  ) : null}
                </div>
                <div className="block xl:flex xl:gap-4">
                  <div className="w-full xl:w-1/2">
                    <Form.Item
                      required
                      name="province"
                      label="Provinsi"
                      className="font-bold mb-1"
                      key={`${formik.values.province}_input_province`}
                      valuePropName="province"
                    >
                      <Select
                        data-testid="select_province"
                        placeholder="Pilih Provinsi"
                        className="selector h-full"
                        status={formik.errors.province && formik.touched.province ? 'error' : ''}
                        virtual={false}
                        onChange={(val: string) => {
                          formik.setFieldValue('city', '')
                          formik.setFieldValue('province', val)
                        }}
                        value={formik.values.province || null}
                      >
                        {provinces.map(province => {
                          return (
                            <Select.Option
                              data-testid={`select_province_${province.name}`}
                              key={province.name}
                              value={province.name}
                            >
                              {province.name}
                            </Select.Option>
                          )
                        })}
                      </Select>
                    </Form.Item>
                    {formik.errors.province && formik.touched.province ? (
                      <ErrorHintForm
                        errorMessage={formik.errors.province}
                        dataTestId="error_message_select_province"
                      />
                    ) : null}
                  </div>
                  <div className="w-full xl:w-1/2">
                    <Form.Item
                      required
                      name="city"
                      label="Kota/Kabupaten"
                      className="font-bold mb-1"
                      key={`${formik.values.city}_input_city`}
                      valuePropName="city"
                    >
                      <Select
                        data-testid="select_city"
                        placeholder="Pilih Kota/Kabupaten"
                        className="selector h-full"
                        status={formik.errors.city && formik.touched.city ? 'error' : ''}
                        virtual={false}
                        onChange={(val: string) => {
                          formik.setFieldValue('city', val)
                        }}
                        value={formik.values.city || null}
                      >
                        {provinces
                          .find(province => province.name === formik.values.province)
                          ?.cities.map(city => {
                            return (
                              <Select.Option
                                key={city.name}
                                value={city.name}
                                data-testid={`select_city_${city.name}`}
                              >
                                {city.name}
                              </Select.Option>
                            )
                          })}
                      </Select>
                    </Form.Item>
                    {formik.errors.city && formik.touched.city ? (
                      <ErrorHintForm
                        errorMessage={formik.errors.city}
                        dataTestId="error_message_select_stock_location_city"
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="hidden xl:block border-l-[#E5E5E7] border-solid border-1 h-auto border-r-0 border-t-0 border-b-0"></div>

              <div className="xl:w-1/2">
                <div>
                  <Form.Item
                    required
                    name="invoiceCode"
                    label="Kode Invoice"
                    className="font-bold mb-1"
                    valuePropName="invoicecode"
                  >
                    <Input
                      data-testid="input_invoiceCode"
                      placeholder="Contoh: PMKS"
                      className={classNameInput('invoiceCode', formik)}
                      status={
                        formik.errors.invoiceCode && formik.touched.invoiceCode ? 'error' : ''
                      }
                      onChange={val =>
                        formik.setFieldValue('invoiceCode', val.target.value.toUpperCase())
                      }
                      value={formik.values.invoiceCode}
                    />
                  </Form.Item>
                  {formik.errors.invoiceCode && formik.touched.invoiceCode ? (
                    <ErrorHintForm
                      errorMessage={formik.errors.invoiceCode}
                      dataTestId="error_message_input_invoiceCode"
                    />
                  ) : null}
                </div>
                <div className="block xl:flex xl:gap-4">
                  <div className="w-full xl:w-1/2">
                    <Form.Item
                      required={false}
                      name="latitude"
                      label="Latitude"
                      className="font-bold mb-1"
                      valuePropName="latitude"
                    >
                      <NumericFormat
                        data-testid="input_latitude"
                        placeholder="Contoh: 0.5245415"
                        className={classNameInput('latitude', formik)}
                        customInput={Input}
                        allowNegative={true}
                        onValueChange={val => {
                          formik.setFieldValue('latitude', val.floatValue)
                        }}
                        value={formik.values.latitude}
                      />
                    </Form.Item>
                    {formik.errors.latitude && formik.touched.latitude ? (
                      <ErrorHintForm
                        errorMessage={formik.errors.latitude}
                        dataTestId="error_message_input_latitude"
                      />
                    ) : null}
                  </div>
                  <div className="w-full xl:w-1/2">
                    <Form.Item
                      required={false}
                      name="longitude"
                      label="longitude"
                      className="font-bold mb-1"
                      valuePropName="longitude"
                    >
                      <NumericFormat
                        data-testid="input_longitude"
                        placeholder="Contoh: 101.9209609"
                        className={classNameInput('longitude', formik)}
                        customInput={Input}
                        allowNegative={true}
                        onValueChange={val => formik.setFieldValue('longitude', val.floatValue)}
                        value={formik.values.longitude}
                      />
                    </Form.Item>
                    {formik.errors.longitude && formik.touched.longitude ? (
                      <ErrorHintForm
                        errorMessage={formik.errors.longitude}
                        dataTestId="error_message_input_longitude"
                      />
                    ) : null}
                  </div>
                </div>
                <div className="block xl:flex xl:gap-4">
                  <div className="w-full xl:w-1/2">
                    <Form.Item
                      required
                      name="openHour"
                      label="Jam Buka"
                      className="font-bold mb-1"
                      valuePropName="openhour"
                    >
                      <TimePicker
                        data-testid="input_open_hour"
                        className={`w-full ${classNameInput('openHour', formik)}`}
                        inputReadOnly={true}
                        showNow={false}
                        allowClear={false}
                        format={'HH:mm'}
                        minuteStep={30}
                        status={formik.errors.openHour && formik.touched.openHour ? 'error' : ''}
                        onChange={val => {
                          if (dayjs(val).isAfter(dayjs(formik.values.closeHour, 'HH:mm'))) {
                            formik.setFieldValue('closeHour', '')
                          }
                          formik.setFieldValue('openHour', val ? dayjs(val).format('HH:mm') : '')
                        }}
                        value={
                          formik.values.openHour ? dayjs(formik.values.openHour, 'HH:mm') : null
                        }
                      />
                    </Form.Item>
                    {formik.errors.openHour && formik.touched.openHour ? (
                      <ErrorHintForm
                        errorMessage={formik.errors.openHour}
                        dataTestId="error_message_input_openHour"
                      />
                    ) : null}
                  </div>
                  <div className="w-full xl:w-1/2">
                    <Form.Item
                      required
                      name="closeHour"
                      label="Jam Tutup"
                      className="font-bold mb-1"
                      valuePropName="closehour"
                    >
                      <TimePicker
                        data-testid="input_close_hour"
                        className={`w-full ${classNameInput('closeHour', formik)}`}
                        inputReadOnly={true}
                        showNow={false}
                        allowClear={false}
                        format={'HH:mm'}
                        minuteStep={30}
                        status={formik.errors.closeHour && formik.touched.closeHour ? 'error' : ''}
                        onChange={val =>
                          formik.setFieldValue('closeHour', val ? dayjs(val).format('HH:mm') : '')
                        }
                        value={
                          formik.values.closeHour ? dayjs(formik.values.closeHour, 'HH:mm') : null
                        }
                        disabledTime={() => {
                          return {
                            disabledHours: () => {
                              if (formik.values.openHour !== '') {
                                const openHour = dayjs(formik.values.openHour, 'HH:mm').hour()
                                return Array.from(Array(openHour).keys())
                              }
                              return []
                            },
                            disabledMinutes: () => {
                              if (formik.values.openHour !== '') {
                                const openMinutes = dayjs(formik.values.openHour, 'HH:mm').minute()
                                return Array.from(Array(openMinutes).keys())
                              }
                              return []
                            },
                            disabledSeconds: () => {
                              return []
                            },
                          }
                        }}
                      />
                    </Form.Item>
                    {formik.errors.closeHour && formik.touched.closeHour ? (
                      <ErrorHintForm
                        errorMessage={formik.errors.closeHour}
                        dataTestId="error_message_input_closeHour"
                      />
                    ) : null}
                  </div>
                </div>
                <div className="block">
                  <Form.Item
                    name="imageUrlItems"
                    valuePropName="imageUrlItems"
                    label="Foto Lokasi"
                    className="font-bold mb-1"
                  >
                    <Upload.Dragger
                      data-testid="input_imageUrlItems"
                      {...imageProps}
                      className="pb-5 font-medium"
                    >
                      <div className="my-0 font-xs flex justify-center">
                        <DocumentDownload color="#3C5337" />
                        <span className="md:pl-3">
                          Tarik foto kesini atau klik <span className="text-[#3C7098]">Upload</span>{' '}
                          untuk upload foto
                        </span>
                      </div>
                      <p className="mt-3 mb-0 font-xs text-[#858C95]">
                        Format: PNG, JPG,JPEG. Maksimal file 2MB
                      </p>
                    </Upload.Dragger>
                  </Form.Item>
                  {formik.errors.imageUrlItems && formik.touched.imageUrlItems ? (
                    <ErrorHintForm
                      errorMessage={formik.errors.imageUrlItems}
                      dataTestId="error_message_input_imageUrlItems"
                    />
                  ) : null}
                </div>

                <Row gutter={16} className="mt-2">
                  <Col span={12}>
                    <div className="flex gap-2 text-[#20202080]">
                      <Switch
                        data-testid="input_isShownMember"
                        className="mb-4"
                        checked={formik.values.isShownMember}
                        onChange={checked => formik.setFieldValue('isShownMember', checked)}
                      />
                      <span className="text-sm text-[#202020] font-medium">
                        Tampilkan di aplikasi Anggota
                      </span>
                    </div>
                    <div className="flex gap-2 text-[#20202080]">
                      <Switch
                        data-testid="input_isShownWorker"
                        className="mb-4"
                        checked={formik.values.isShownWorker}
                        onChange={checked => formik.setFieldValue('isShownWorker', checked)}
                      />
                      <span className="text-sm text-[#202020] font-medium">
                        Tampilkan di aplikasi Pekerja
                      </span>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="flex gap-2 text-[#20202080]">
                      <Switch
                        data-testid="input_status"
                        className="mb-4"
                        checked={formik.values.status === StockLocationStatusEnum.Active}
                        onChange={checked =>
                          formik.setFieldValue(
                            'status',
                            checked
                              ? StockLocationStatusEnum.Active
                              : StockLocationStatusEnum.Inactive,
                          )
                        }
                      />
                      <span className="text-sm text-[#202020] font-medium">Lokasi Aktif</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Form>
        </div>
      </Card>
    </Modal>
  )
}

export default ModalFormStockLocation
